import {
  Drawer,
  DrawerContent,
  DrawerDescription,
  DrawerHeader,
  DrawerTitle,
} from "@/components/ui/drawer";
import { AndroidInstallInstructions } from "@/AndroidInstallInstructions";
import { IOSInstallInstructions } from "@/IOSInstallInstructions";
import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from "react";

const INSTALL_HELP_DISMISSED_KEY = "music.kennedyryon.installHelpDismissed";

function isIOS(): boolean {
  if (typeof navigator === "undefined") return false;
  const ua = navigator.userAgent || "";
  return (
    /iPad|iPhone|iPod/.test(ua) ||
    (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1)
  );
}

function isAndroid(): boolean {
  if (typeof navigator === "undefined") return false;
  return /android/i.test(navigator.userAgent || "");
}

export function isInstallHelpDismissed(): boolean {
  if (typeof window === "undefined") return false;
  try {
    return localStorage.getItem(INSTALL_HELP_DISMISSED_KEY) === "1";
  } catch {
    return false;
  }
}

export function setInstallHelpDismissed(): void {
  try {
    localStorage.setItem(INSTALL_HELP_DISMISSED_KEY, "1");
  } catch {}
}

export type InstallHelpHandle = {
  showDialog: (open?: boolean) => void;
};

export const InstallHelp = forwardRef<InstallHelpHandle, { appInstalled: boolean }>(function InstallHelp(
  { appInstalled },
  ref,
) {
  const [open, setOpen] = useState(false);
  const [playbackStarted, setPlaybackStarted] = useState(false);
  const [confirmDismiss, setConfirmDismiss] = useState(false);
  const videoRef = useRef<HTMLVideoElement | null>(null);

  useImperativeHandle(ref, () => ({
    showDialog: (next?: boolean) => {
      const shouldOpen = next !== false;
      if (shouldOpen && isInstallHelpDismissed()) return;
      setOpen(shouldOpen);
    },
  }));

  useEffect(() => {
    if (!open) {
      videoRef.current?.pause();
      setPlaybackStarted(false);
      setConfirmDismiss(false);
    }
  }, [open]);

  return (
    <Drawer dismissible={false} open={open} onOpenChange={setOpen} modal>
      <DrawerContent hideHandle className="overflow-hidden px-0 pb-0">
        <DrawerHeader className="shrink-0 pt-4 pb-2">
          <DrawerTitle className="text-center">
            {playbackStarted ? "Add to Home Screen" : "Tap Play"}
          </DrawerTitle>
          <DrawerDescription className="text-center">
          </DrawerDescription>
        </DrawerHeader>

        <div className="min-h-0 flex-1 overflow-y-auto px-4 pb-[max(1rem,calc(1rem+env(safe-area-inset-bottom)))]">
          <video
            ref={videoRef}
            className="mx-auto block rounded-lg bg-black object-contain"
            poster="/assets/video/instruct-cover.jpg"
            preload="none"
            src="/assets/video/instruct.mov"
            controls
            playsInline
            width={300}
            height={300}
            onPlay={() => setPlaybackStarted(true)}
          />

          {playbackStarted ? (
            <>
              {isIOS() ? <IOSInstallInstructions /> : null}
              {isAndroid() ? <AndroidInstallInstructions appInstalled={appInstalled} /> : null}

              <div className="py-4">
                {confirmDismiss ? (
                  <div className="space-y-3">
                    <p className="text-center text-sm text-muted-foreground">
                      Are you sure? You won't see this guide again.
                    </p>
                    <div className="flex gap-2">
                      <button
                        type="button"
                        className="inline-flex h-11 flex-1 cursor-pointer items-center justify-center rounded-md border border-input bg-background px-4 text-sm font-medium text-foreground ring-offset-background transition-colors hover:bg-accent hover:text-accent-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                        onClick={() => setConfirmDismiss(false)}
                      >
                        Cancel
                      </button>
                      <button
                        type="button"
                        className="inline-flex h-11 flex-1 cursor-pointer items-center justify-center rounded-md border border-input bg-background px-4 text-sm font-medium text-foreground ring-offset-background transition-colors hover:bg-accent hover:text-accent-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                        onClick={() => {
                          setInstallHelpDismissed();
                          setOpen(false);
                        }}
                      >
                        Yes, dismiss
                      </button>
                    </div>
                  </div>
                ) : (
                  <button
                    type="button"
                    className="inline-flex h-11 w-full cursor-pointer items-center justify-center rounded-md border border-input bg-background px-4 text-sm font-medium text-foreground ring-offset-background transition-colors hover:bg-accent hover:text-accent-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50"
                    onClick={() => setConfirmDismiss(true)}
                  >
                    Dismiss
                  </button>
                )}
              </div>
            </>
          ) : null}
        </div>
      </DrawerContent>
    </Drawer>
  );
});
